<template>
  <div class="cbc-widget">
    <div v-if="loading" class="text-center py-4">
      <v-progress-circular indeterminate size="28" color="blue darken-1" />
    </div>
    <div v-else-if="fetchError" class="text-center py-4">
      <v-icon color="grey lighten-1" size="32">mdi-alert-circle-outline</v-icon>
      <p class="text-caption grey--text mt-2">Unable to load data</p>
    </div>
    <div v-else class="kpi-card-body">
      <div class="d-flex align-center justify-space-between mb-3">
        <div>
          <div class="text-subtitle-2 font-weight-bold grey--text text--darken-2">
            Collections
          </div>
          <div class="text-caption grey--text">Current month by category</div>
        </div>
        <div class="text-right">
          <div class="kpi-total" :class="toneText('blue')">{{ formatCurrency(total) }}</div>
          <div class="text-caption grey--text">Total</div>
        </div>
      </div>

      <div v-if="chartSeries.length > 0" class="chart-wrap">
        <!-- eslint-disable-next-line import/no-unresolved -->
        <apexchart
          :key="isDark ? 'dark' : 'light'"
          type="donut"
          height="220"
          :options="chartOptions"
          :series="chartSeries"
        />
      </div>

      <div class="legend-grid mt-2">
        <div
          v-for="(cat, i) in categories"
          :key="cat.category"
          class="d-flex align-center mb-1"
        >
          <span class="legend-dot mr-2" :style="`background:${sliceColor(i)}`" />
          <span class="text-caption flex-grow-1" :class="toneText('grey')">
            {{ cat.category }}
          </span>
          <span
            class="text-caption font-weight-bold"
            :style="`color:${sliceColor(i)}`"
          >
            {{ formatCurrency(cat.amount) }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// eslint-disable-next-line import/no-unresolved
import VueApexCharts from 'vue-apexcharts';
import { clients } from '../../../util/clients';
import WidgetThemeMixin from '../WidgetTheme.mixin';

const { backendRest } = clients.direct;

/* One palette per theme, shared by the donut slices, the legend dots and the legend amounts, so a
   category is the same colour in all three. The dark set is the same six hues at a lighter shade:
   the slices would survive the darker set, but the amounts beside them are small bold text and
   #1976d2 or #3949ab on #1e1e1e is around 3:1. */
const COLORS = ['#1976d2', '#3949ab', '#f57c00', '#00897b', '#8e24aa', '#ffa000'];
const COLORS_DARK = ['#64b5f6', '#9fa8da', '#ffb74d', '#4db6ac', '#ce93d8', '#ffd54f'];

export default {
  name: 'CollectionsByCategoryWidget',
  components: {
    apexchart: VueApexCharts,
  },
  mixins: [WidgetThemeMixin],
  data: () => ({
    loading: true,
    fetchError: false,
    total: 0,
    categories: [],
  }),
  computed: {
    colors() {
      return this.isDark ? COLORS_DARK : COLORS;
    },
    chartSeries() {
      return this.categories.map((c) => Math.round(c.amount * 100) / 100);
    },
    chartOptions() {
      const self = this;
      return {
        chart: {
          type: 'donut',
          animations: { enabled: true },
          toolbar: { show: false },
          // Without this ApexCharts draws its own white canvas behind the donut.
          background: 'transparent',
        },
        theme: { mode: this.isDark ? 'dark' : 'light' },
        labels: this.categories.map((c) => c.category),
        colors: this.colors,
        legend: { show: false },
        dataLabels: { enabled: false },
        plotOptions: {
          pie: {
            donut: {
              size: '65%',
              labels: {
                show: true,
                total: {
                  show: true,
                  label: 'Total',
                  fontSize: '12px',
                  color: this.isDark ? 'rgba(255, 255, 255, 0.6)' : '#757575',
                  formatter() {
                    const t = self.total;
                    if (t >= 1000000) return `$${(t / 1000000).toFixed(1)}M`;
                    if (t >= 1000) return `$${(t / 1000).toFixed(0)}K`;
                    return `$${t.toFixed(0)}`;
                  },
                },
              },
            },
          },
        },
        tooltip: {
          y: {
            formatter(val) {
              return `$${val.toLocaleString('en-US', {
                minimumFractionDigits: 2, maximumFractionDigits: 2,
              })}`;
            },
          },
        },
      };
    },
  },
  async created() {
    await this.fetchData();
  },
  methods: {
    sliceColor(i) {
      return this.colors[i % this.colors.length];
    },
    async fetchData() {
      this.loading = true;
      try {
        const response = await backendRest.get('/dashboard/collections-by-category');
        const data = response.data || {};
        this.total = data.total || 0;
        this.categories = data.categories || [];
      } catch (e) {
        console.error('CollectionsByCategoryWidget fetch failed', e);
        this.fetchError = true;
        this.total = 0;
        this.categories = [];
      } finally {
        this.loading = false;
      }
    },
    formatCurrency(val) {
      const n = val || 0;
      if (n >= 1000000) return `$${(n / 1000000).toFixed(2)}M`;
      if (n >= 1000) return `$${(n / 1000).toFixed(1)}K`;
      return `$${n.toFixed(2)}`;
    },
    async refresh() {
      await this.fetchData();
    },
  },
};
</script>

<style scoped>
.cbc-widget { position: relative; }
.kpi-card-body { padding: 4px 0; }
.kpi-total {
  font-size: 1.6rem; font-weight: 800;
  line-height: 1; letter-spacing: -1px;
}
.chart-wrap { margin: 0 -8px; }
.legend-grid { padding: 0 2px; }
.legend-dot {
  width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0;
}
</style>
